// Importing necessary React and Material-UI components and styles
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { connect } from 'react-redux';
import { styled } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import MailIcon from '@mui/icons-material/Mail';

import { Case } from '@apps/shared/src/caseSelect/types/myCase';
import { resetMemberInfo } from '@apps/shared/src/getMember/actions';
import {
  CaseFilter,
  getMyCases,
  getCasesForClient,
  resetMyCasesState,
  updateSelectedPlan,
} from '@apps/shared/src/caseSelect/actions';
import { setCaseType, updateCaseMemberDetail } from '@apps/shared/src/addCase/actions';
import { MemberInfo } from '@apps/shared/src/getMember/types/memberTypes';
import { getMasterPlansForCaseView } from '../providerSearch/actions';

import MyCasesFilter from './myCasesFilter';
import AppLayout from '../shared/AppLayout';
import PageSpecificHeader from '../shared/PageSpecificHeader';
import CaseDetailModal from './caseDetailModal';
import TypeSelectionModal from '../caseSubmission/typeSelectionModal';
import { RootState } from '../store';

// Styled component for pagination to fix overflow issues
const StyledTablePagination = styled(TablePagination)({
  position: 'sticky',
  width: 'auto',
  borderTop: '1px solid #ECECEC',
  backgroundColor: '#fff',
  borderRadius: '0 0 .5rem .5rem',
  bottom: '0',
  right: '2.5rem',
  overflow: 'visible', // Changed from 'hidden' to prevent clipping
  minHeight: '52px', // Ensure enough height for controls
  '& .MuiSelect-selectMenu': {
    backgroundColor: 'white !important',
    width: 'auto',
    '&:focus': {
      border: 'none',
    },
  },
  '& .MuiTablePagination-toolbar': {
    minHeight: '52px', // Increased from .5rem to prevent overlap
    paddingRight: '16px',
  },
});

// Creating the Material-UI styles using makeStyles
const useStyles = makeStyles((theme: any) => ({
  spinner: {
    width: '100%',
    marginTop: '2rem',
  },
  container: {
    display: 'flex',
    justifyContent: 'space-between',
    // borderBottom: '1px solid #ECECEC',
  },
  recentCase: {
    fontSize: '28px',
    fontWeight: 600,
    lineHeight: '1.5rem',
    letterSpacing: '0px',
    textAlign: 'left',
    color: '#36383D',
  },
  subtitle: {
    fontSize: '20px',
    fontWeight: 400,
    lineHeight: '40px',
    letterSpacing: '0px',
    textAlign: 'left',
    color: '#797C80',
  },
  submitCaseBtn: {
    margin: '1.4rem 0 0 1rem',
    backgroundColor: '#F07037',
    color: '#EFECE7',
    height: '40px',
    fontSize: '16px',
    fontWeight: 700,
    marginRight: '20px',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#F07037',
    },
  },
  paper: {
    position: 'relative',
    border: '1px solid #E8E8E8',
    borderRadius: '8px',
    width: '75%',
    height: 'calc(100vh - 70px - 50px - 7rem)',
    '&::-webkit-scrollbar': {
      width: '1px',
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: '1px',
    },
  },
  contentHeight: {
    height: 'calc(100% - 52px)', // Adjusted to account for pagination height
    '&::-webkit-scrollbar': {
      width: '1px',
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: '1px',
    },
  },

  headerTable: {
    position: 'sticky',
    top: 0,
    backgroundColor: '#fff',
  },
  filterAndTable: {
    display: 'flex',
    gap: '2rem',
    paddingRight: '1.2rem',
  },
  tableHead: {
    borderRadius: '0.5rem',
    height: '40px',
    fontSize: '12px',
    fontWeight: 600,
    lineHeight: '16px',
    letterSpacing: '0.04em',
    textAlign: 'left',
    color: '#333',
  },
  statusCenter: {
    paddingLeft: '3.8rem',
  },
  status: {
    width: '7.5rem',
    height: '32px',
    borderRadius: '1rem',
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: '18px',
    padding: '1px 10px 1px 10px',
    textTransform: 'uppercase',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  customTooltip: {
    backgroundColor: '#ffffff',
    color: '#5f5f5f',
    boxShadow:
      '0px 2px 4px -1px rgba(0,0,0,0.2),0px 4px 5px 0px rgba(0,0,0,0.14),0px 1px 10px 0px rgba(0,0,0,0.12)',
    border: 'none',
    fontSize: '14px',
  },

  blue: {
    backgroundColor: '#E4EEFF',
    color: '#366BED',
  },

  grey: {
    backgroundColor: '#E8E8E8',
    color: '#787373',
  },

  expandDownIcon: {
    width: '24px',
    height: '24px',
    color: '#F97140',
    fontSize: '18px',
    fontWeight: 800,
    cursor: 'pointer',
  },
}));

type StateProps = {
  cases: Case[];
  hasLoadedCases: boolean;
  isCasesLoading: boolean;
  hasMemberViewRole: boolean;
  hasMemberSubmitRole: boolean;
  hasClientViewRole: boolean;
  hasClientSubmitRole: boolean;
  selectedPlan: string;
  plans: any;
};

const mapStateToProps = ({ caseSelect, planSelect, user }: RootState): StateProps => ({
  cases: caseSelect.Cases,
  hasLoadedCases: caseSelect.hasLoadedCases,
  isCasesLoading: caseSelect.isCasesLoading,
  selectedPlan: caseSelect.selectedPlan,
  plans: planSelect.masterPlansForCaseView,
  hasMemberViewRole: user.currentUser?.roles?.includes('medivi-casesmember') ?? false,
  hasMemberSubmitRole: user.currentUser?.roles?.includes('medivi-casesubmissionmember') ?? false,
  hasClientViewRole: user.currentUser?.roles?.includes('medivi-caseviewclient') ?? false,
  hasClientSubmitRole: user.currentUser?.roles?.includes('medivi-casesubmissionclient') ?? false,
});

type DispatchProps = {
  getMyCases: (filter: CaseFilter) => void;
  getCasesForClient: (filter: CaseFilter) => void;
  resetMyCasesState: () => void;
  setCaseType: (caseType: string) => void;
  updateSelectedPlan: (id: string) => void;
  updateCaseMemberDetail: (memberInfo: MemberInfo) => void;
  resetMemberInfo: () => void;
  getMasterPlansForCaseView: () => void;
};

const mapDispatchToProps = {
  setCaseType,
  getMyCases,
  getCasesForClient,
  resetMyCasesState,
  updateSelectedPlan,
  updateCaseMemberDetail,
  resetMemberInfo,
  getMasterPlansForCaseView,
};

type Props = StateProps & DispatchProps;

type HeadCell = {
  label: string;
  id: keyof Case | string; // Updated to allow for nested properties
  numeric: boolean;
};

const headCells: HeadCell[] = [
  { id: 'First_Name__c', numeric: false, label: 'MEMBER NAME' },
  { id: 'Member_ID__c', numeric: false, label: 'MEMBER ID' },
  { id: 'Hospital__r', numeric: false, label: 'PROVIDER NAME' },
  { id: 'RecordType', numeric: false, label: 'CASE TYPE' },
  { id: 'CaseNumber', numeric: false, label: 'CASE ID' },
  { id: 'CreatedDate', numeric: false, label: 'CREATED ON' },
  { id: 'Status', numeric: false, label: 'STATUS' },
];

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
  if (orderBy === 'Hospital__r' || orderBy === 'RecordType') {
    const aValue = a[orderBy].Name;
    const bValue = b[orderBy].Name;
    // If both values are undefined, consider them equal
    if (aValue === undefined && bValue === undefined) {
      return 0;
    }

    // If only one value is undefined, treat it as greater than the other
    if (aValue === undefined) {
      return -1;
    }
    if (bValue === undefined) {
      return 1;
    }
    if (b[orderBy].Name < a[orderBy].Name) {
      return -1;
    }
    if (b[orderBy].Name > a[orderBy].Name) {
      return 1;
    }
    return 0;
  }
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator<Key extends keyof any>(
  order: 'asc' | 'desc',
  orderBy: Key
): (a: { [key in Key]: number | string }, b: { [key in Key]: number | string }) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: Case[], comparator: (a: T, b: T) => number) {
  const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map(el => el[0]);
}

type EnhancedTableProps = {
  hasClientViewRole: boolean;
  classes: ReturnType<typeof useStyles>;
  onRequestSort: (event: React.MouseEvent<unknown>, property: keyof Case) => void;
  order: 'asc' | 'desc';
  orderBy: keyof Case | '';
};

function EnhancedTableHead(props: EnhancedTableProps) {
  const { classes, order, orderBy, onRequestSort, hasClientViewRole } = props;
  const createSortHandler = (property: keyof Case) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property);
  };

  // Conditionally modify headCells based on hasClientViewRole
  const filteredHeadCells = hasClientViewRole
    ? headCells // When hasClientViewRole is true, show all columns, including MEMBER NAME and MEMBER ID
    : headCells.filter(
        headCell => headCell.id !== 'First_Name__c' && headCell.id !== 'Member_ID__c'
      );

  return (
    <TableHead>
      <TableRow className={classes.headerTable}>
        <TableCell style={{ textAlign: 'center', height: '40px', borderRadius: '.5rem' }}>
          #
        </TableCell>
        {filteredHeadCells.map(headCell => (
          <TableCell
            key={headCell.id}
            align="left"
            className={`${classes.tableHead} ${
              headCell.id === 'Status' ? classes.statusCenter : ''
            }`}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : 'asc'}
              onClick={createSortHandler(headCell.id as keyof Case)} // Cast to keyof Case
            >
              {headCell.label}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

// Defining the main functional component for MyCases
const MyCases = ({
  cases,
  selectedPlan,
  hasClientViewRole,
  hasClientSubmitRole,
  hasMemberViewRole,
  hasMemberSubmitRole,
  plans,
  updateSelectedPlan,
  updateCaseMemberDetail,
  getMyCases,
  getCasesForClient,
  getMasterPlansForCaseView,
  hasLoadedCases,
  isCasesLoading,
  setCaseType,
  resetMyCasesState,
  resetMemberInfo,
}: Props) => {
  const classes = useStyles();
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [orderBy, setOrderBy] = useState<keyof Case>('CaseNumber');
  const [caseDetailModalOpen, setCaseDetailModalOpen] = useState(false);
  const [caseSubmissionModalOpen, setCaseSubmissionModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedCase, setSelectedCase] = useState('');
  const [fetchTrigger, setFetchTrigger] = useState(0);
  const [appliedFilter, setAppliedFilter] = useState<CaseFilter>({
    queryParam: '',
    type: 'All',
    createdDate: '',
    status: 'All',
    planID: '',
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const history = useHistory();

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof Case) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Function to sort cases based on the currently selected criteria
  const sortedCases = stableSort(cases, getComparator(order, orderBy));

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const openCaseDetailModal = (id: string) => {
    setSelectedCase(id);
    setCaseDetailModalOpen(false);
    setLoading(true);
  };

  const handleEmailClick = (
    e: any,
    PSSEmail: string,
    CSSEmail: string,
    caseNumber: string,
    recordType: string
  ) => {
    e.stopPropagation();
    const emailToSend = hasClientViewRole ? CSSEmail || '' : PSSEmail || '';
    const subject = `${recordType} - ${caseNumber}`;

    const mailtoLink = `mailto:${emailToSend}?subject=${encodeURIComponent(subject)}`;

    window.location.href = mailtoLink;
  };

  const handleCaseSubmissionModal = () => {
    setCaseSubmissionModalOpen(true);
  };

  const handleCallAheadClick = () => {
    setCaseType('Call Ahead');
    history.push(`/member-detail?param=Call Ahead`);
  };

  const getStatusLabel = (status: string) => {
    let caseStatus;

    if (status === 'Intake' || status === 'Opened' || status === 'opened') {
      caseStatus = 'Opened';
    } else if (status === 'Closed' || status === 'closed') {
      caseStatus = 'Closed';
    } else {
      caseStatus = 'Processing';
    }
    return caseStatus;
  };

  const formatDate = (inputDate: any) => {
    if (inputDate !== '') {
      const dateObject = new Date(inputDate);
      const datePart = dateObject.toISOString().split('T')[0];
      const dateComponents = datePart.split('-');
      if (dateComponents.length === 3) {
        const year = dateComponents[0];
        const month = dateComponents[1];
        const day = dateComponents[2];
        return `${month}/${day}/${year}`;
      }
    }
    return '';
  };

  useEffect(() => {
    resetMemberInfo();
    if (hasClientViewRole) {
      getCasesForClient({
        queryParam: appliedFilter.queryParam || '',
        type: appliedFilter.type === 'All' ? '' : appliedFilter.type,
        createdDate: appliedFilter.createdDate || '',
        status: appliedFilter.status === 'All' ? '' : appliedFilter.status,
        planID: selectedPlan || '',
      });
    } else {
      getMyCases({
        queryParam: '',
        type: '',
        createdDate: '',
        status: '',
        planID: selectedPlan || '',
      });
    }
    setAppliedFilter((prevState: any) => ({
      ...prevState,
      planID: selectedPlan || '',
    }));
  }, [getMyCases, getCasesForClient, selectedPlan]);

  useEffect(() => {
    if (hasClientViewRole) {
      getMasterPlansForCaseView();
    }
  }, [getMasterPlansForCaseView]);

  useEffect(() => {
    if (selectedPlan === '') {
      updateSelectedPlan(plans && plans.length > 0 ? plans[0].id : '');
    }
  }, [plans]);

  return (
    <AppLayout>
      {caseSubmissionModalOpen && (
        <TypeSelectionModal
          caseSubmissionModalOpen={caseSubmissionModalOpen}
          setCaseSubmissionModalOpen={setCaseSubmissionModalOpen}
        />
      )}
      <div className={classes.container}>
        <PageSpecificHeader
          title="Recent Cases"
          description="View recent cases and submit a new case"
        />

        <div>
          {((hasMemberViewRole && hasMemberSubmitRole) ||
            (hasClientViewRole && hasClientSubmitRole)) && (
            <Tooltip
              title={
                <>
                  Call ahead is designed for those actively seeking to make an appointment with a
                  physician you are seeing for the first time on this new health plan. You should
                  have either:
                  <br />
                  a&#41; A previously scheduled appointment within the next 30 days
                  <br />
                  b&#41; The need to call in the next two weeks to schedule a new appointment
                </>
              }
              classes={{
                tooltip: classes.customTooltip,
              }}
            >
              <Button
                variant="contained"
                className={classes.submitCaseBtn}
                onClick={handleCallAheadClick}
              >
                Call Ahead
              </Button>
            </Tooltip>
          )}

          {((hasMemberViewRole && hasMemberSubmitRole) ||
            (hasClientViewRole && hasClientSubmitRole)) && (
            <Button
              variant="contained"
              className={classes.submitCaseBtn}
              onClick={handleCaseSubmissionModal}
            >
              SUBMIT A CASE
            </Button>
          )}
        </div>
      </div>
      <div className={classes.filterAndTable}>
        <MyCasesFilter
          getMyCases={getMyCases}
          isClient={hasClientViewRole}
          plans={plans}
          getCasesForClient={getCasesForClient}
          resetMyCasesState={resetMyCasesState}
          selectedPlan={selectedPlan}
          appliedFilter={appliedFilter}
          setAppliedFilter={setAppliedFilter}
          updateCaseMemberDetail={updateCaseMemberDetail}
          updateSelectedPlan={updateSelectedPlan}
          setPage={setPage}
          setRowsPerPage={setRowsPerPage}
        />
        <Paper className={classes.paper}>
          <TableContainer className={classes.contentHeight}>
            <Table>
              <EnhancedTableHead
                hasClientViewRole={hasClientViewRole}
                classes={classes}
                onRequestSort={handleRequestSort}
                order={order}
                orderBy={orderBy}
              />
              <TableBody>
                {(isCasesLoading || loading) && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <CircularProgress
                        variant="determinate"
                        value={75}
                        className={classes.spinner}
                      />
                    </TableCell>
                  </TableRow>
                )}
                {!isCasesLoading && !loading && (cases === undefined || cases.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography>No Cases Found</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {!isCasesLoading &&
                  !loading &&
                  sortedCases &&
                  sortedCases.length > 0 &&
                  sortedCases
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((myCase: any, index: number) => (
                      <TableRow
                        key={myCase.id}
                        onClick={() => openCaseDetailModal(myCase.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <TableCell
                          style={{ fontSize: '14px', textAlign: 'center', fontWeight: 600 }}
                        >
                          {page * rowsPerPage + index + 1}
                        </TableCell>
                        {hasClientViewRole && (
                          <TableCell style={{ fontSize: '14px', fontWeight: 600 }}>
                            {`${myCase.First_Name__c} ${myCase.Last_Name__c}`}
                          </TableCell>
                        )}
                        {hasClientViewRole && (
                          <TableCell style={{ fontSize: '14px', fontWeight: 600 }}>
                            {myCase.Member_ID__c}
                          </TableCell>
                        )}
                        <TableCell style={{ fontSize: '14px', fontWeight: 600 }}>
                          {myCase.Hospital__r.Name}
                        </TableCell>
                        <TableCell>
                          {(() => {
                            if (myCase.RecordType.Name === '6DH Employee Balance Bill') {
                              return 'Balance Bill';
                            }
                            if (myCase.RecordType.Name === '6DH Employee Access Issue') {
                              return 'Access Issue';
                            }
                            if (myCase.RecordType.Name === '6DH Employee Call Ahead') {
                              return 'Call Ahead';
                            }
                            return myCase.RecordType.Name;
                          })()}
                        </TableCell>
                        <TableCell>{myCase.CaseNumber}</TableCell>
                        <TableCell>{formatDate(myCase.CreatedDate)}</TableCell>
                        <TableCell>
                          <Tooltip
                            title={
                              myCase.Status_Description__c || 'Waiting for additional Information'
                            }
                            classes={{
                              tooltip: classes.customTooltip,
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'start',
                                alignItems: 'center',
                                gap: '16px',
                              }}
                            >
                              <div
                                className={`${classes.status} ${
                                  getStatusLabel(myCase.Status) === 'Processing' ||
                                  getStatusLabel(myCase.Status) === 'Opened'
                                    ? classes.blue
                                    : classes.grey
                                }`}
                              >
                                {getStatusLabel(myCase.Status)}
                              </div>
                              <MailIcon
                                className={classes.expandDownIcon}
                                // eslint-disable-next-line prettier/prettier
                                onClick={e =>
                                  handleEmailClick(
                                    e,
                                    myCase?.PSS__r?.Email,
                                    myCase?.CSS__r?.Email,
                                    myCase?.CaseNumber,
                                    myCase?.RecordType.Name
                                  )
                                }
                              />
                            </div>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
              </TableBody>
            </Table>
          </TableContainer>
          <StyledTablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={cases.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      </div>
      <CaseDetailModal
        isClient={hasClientViewRole}
        caseDetailModalOpen={caseDetailModalOpen}
        setCaseDetailModalOpen={setCaseDetailModalOpen}
        selectedCase={selectedCase}
        setLoading={setLoading}
        loading={loading}
      />
    </AppLayout>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(MyCases);
